import React, { useCallback, useMemo } from 'react';
import type { JSX } from 'react';

import { useGetLists } from '@vlabs/api/artemy/client';
import type { Card } from '@vlabs/api/artemy/model';
import { ArrowInCircleRightIcon } from '@vlabs/icons';
import { ViewCardButton } from '@vlabs/pages/point/cards/view-card';
import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { parseISOToLocale } from '@vlabs/shared/utils/date';
import { Control, CopyButton } from '@vlabs/uikit';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { DeleteCardsIconButtonPoint3 } from '@vlabs/monokit/features/delete-cards';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { ListsPreview } from './lists-preview';
import { PersonCardFacesPoint3 } from './person-card-faces/PersonCardFaces.point3';
import st from './PersonCard.module.sass';
import { GoToCardCircleAnimatedButton } from '../../../features';

export type TPersonCardSize = 'large' | 'small';

interface PersonCardProps {
  card: Card;
  isSelected: boolean;
  size?: TPersonCardSize;
  onSelect?: (personId: string, next: boolean) => void;
  className?: string;
  'data-testid'?: string;
}

const displayName = 'PersonCard';
const testid = buildTestid(displayName);

export function PersonCard({
  card,
  size = 'large',
  isSelected,
  onSelect = undefined,
  className = undefined,
  'data-testid': dataTestId,
}: PersonCardProps): JSX.Element {
  const { t } = useTranslation();
  const getLists = useGetLists({ page_size: 1000 });
  const listsById = useMemo(() => {
    const map: Record<string, string> = {};
    getLists.data?.lists?.forEach((list) => {
      if (list.list_id && list.user_data) {
        map[list.list_id] = list.user_data;
      }
    });
    return map;
  }, [getLists.data]);
  const renderedLists = card.lists?.map(
    (list) => (listsById[list] ?? list),
  );

  const fio = (card.user_data?.fio as string | undefined)?.trim?.() ?? '—';
  const createTime = card.create_time ? (parseISOToLocale(card.create_time) ?? '—') : '—';

  const handleCheckboxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!card.card_id) return;
    onSelect?.(card?.card_id, e.target.checked);
  }, [onSelect, card.card_id]);

  return (
    <div
      className={cn(
        st.Root,
        st[`Size_${size}`],
        isSelected && st.Selected,
        className,
      )}
      {...testid(dataTestId)}
    >
      <div className={st.Faces}>
        {!!card.card_id && (
          <PersonCardFacesPoint3
            cardId={card.card_id}
            size={size}
            {...testid(dataTestId)}
          />
        )}
        {!!card.card_id && (
          <div className={st.GoToContainer}>
            <GoToCardCircleAnimatedButton
              cardId={card.card_id}
              icon={<ArrowInCircleRightIcon />}
              size="small"
            />
          </div>
        )}
      </div>

      <div
        className={st.Header}
        data-testid="personCard.header"
      >
        <Control.Checkbox
          checked={isSelected}
          onChange={handleCheckboxChange}
          {...testid('Checkbox', dataTestId)}
        />

        <div
          className={st.Name}
          data-testid="personCard.name"
          {...testid('Name', dataTestId)}
        >
          <TruncatedText
            hasTruncation
            tooltipContent={fio}
          >
            {fio}
          </TruncatedText>
        </div>
      </div>

      {size === 'large' && (
        <React.Fragment>
          <div
            className={st.Info}
            data-testid="personCard.info"
          >
            <div className={st.Row}>
              <span className={st.Label}>{t('shared:personCard.card_id')}</span>

              <div className={st.Value}>
                <div className={st.TextWrapper}>
                  <TruncatedText
                    hasTruncation
                    tooltipContent={card.card_id ?? ''}
                  >
                    {card.card_id ?? ''}
                    {!!card.card_id && (
                    <CopyButton
                      className={st.CopyButton}
                      round
                      value={card.card_id}
                    />
                    )}
                  </TruncatedText>
                </div>
              </div>
            </div>

            <div className={st.Row}>
              <span className={st.Label}>{t('shared:personCard.created')}</span>
              <span
                className={st.Value}
                data-testid="personCard.createTime"
              >
                {createTime}
              </span>
            </div>
            {(renderedLists?.length ?? 0) > 0 && (
              <div className={st.Row}>
                <span className={st.Label}>{t('shared:personCard.lists')}</span>
                <span
                  className={st.Value}
                  data-testid="personCard.lists"
                  {...testid('Lists', dataTestId)}
                >
                  <ListsPreview items={renderedLists} />
                </span>
              </div>
            )}
          </div>

          <div
            className={st.Actions}
            data-testid="personCard.actions"
          >
            {!!card.card_id && <ViewCardButton cardId={card.card_id} />}
            {!!card.card_id && (
              <DeleteCardsIconButtonPoint3
                cardId={card.card_id}
                flatOverride
              />
            )}
          </div>
        </React.Fragment>
      )}
    </div>
  );
}
